import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { RotateCcw, Building2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import PropertyCard from "@/components/PropertyCard";
import { CATALOGO, CIDADES_ATENDIDAS } from "@/content/site";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIPOS = ["Casa", "Apartamento", "Sobrado", "Terreno", "Casa de Praia"];
const FAIXAS_VENDA = [
  { label: "Todas as faixas", value: "todas" },
  { label: "Até R$ 200 mil", value: "0-200000" },
  { label: "R$ 200 mil — R$ 350 mil", value: "200000-350000" },
  { label: "R$ 350 mil — R$ 500 mil", value: "350000-500000" },
  { label: "Acima de R$ 500 mil", value: "500000-999999999" },
];
const FAIXAS_ALUGUEL = [
  { label: "Todas as faixas", value: "todas" },
  { label: "Até R$ 1.200/mês", value: "0-1200" },
  { label: "R$ 1.200 — R$ 2.000/mês", value: "1200-2000" },
  { label: "Acima de R$ 2.000/mês", value: "2000-999999999" },
];
const QUARTOS = [
  { label: "Qualquer", value: "0" },
  { label: "1+ quartos", value: "1" },
  { label: "2+ quartos", value: "2" },
  { label: "3+ quartos", value: "3" },
];

const selectCls =
  "h-12 rounded-none border-white/10 bg-white/5 font-mono text-[11px] uppercase tracking-[0.15em] text-stone-300 focus:ring-0";
const contentCls = "rounded-none border-white/10 bg-elevated text-stone-200";

const Imoveis = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [negocio, setNegocio] = useState(searchParams.get("negocio") || "todos");
  const [tipo, setTipo] = useState(searchParams.get("tipo") || "todos");
  const [cidade, setCidade] = useState(searchParams.get("cidade") || "todas");
  const [faixa, setFaixa] = useState("todas");
  const [quartos, setQuartos] = useState("0");
  const [imoveis, setImoveis] = useState([]);
  const [loading, setLoading] = useState(true);

  const faixas = negocio === "Aluguel" ? FAIXAS_ALUGUEL : FAIXAS_VENDA;

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (negocio !== "todos") params.negocio = negocio;
    if (tipo !== "todos") params.tipo = tipo;
    if (cidade !== "todas") params.cidade = cidade;
    if (faixa !== "todas") {
      const [min, max] = faixa.split("-");
      params.preco_min = min;
      params.preco_max = max;
    }
    if (quartos !== "0") params.quartos_min = quartos;
    const next = {};
    if (params.negocio) next.negocio = params.negocio;
    if (params.tipo) next.tipo = params.tipo;
    if (params.cidade) next.cidade = params.cidade;
    setSearchParams(next, { replace: true });
    axios
      .get(`${API}/properties`, { params })
      .then((res) => setImoveis(res.data))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [negocio, tipo, cidade, faixa, quartos]);

  const limpar = () => {
    setNegocio("todos");
    setTipo("todos");
    setCidade("todas");
    setFaixa("todas");
    setQuartos("0");
  };

  return (
    <main className="mx-auto max-w-7xl px-6 pb-24 pt-36 lg:px-10 lg:pt-44">
      <SectionHeading overline={CATALOGO.overline} lines={CATALOGO.titulo.map((l) => <>{l}</>)} texto={CATALOGO.texto} large inView={false} />

      <Reveal delay={0.3}>
        <div data-testid="filter-bar" className="mt-12 grid gap-4 border border-white/10 bg-coal/60 p-5 backdrop-blur-xl sm:grid-cols-2 lg:grid-cols-6">
          <Select value={negocio} onValueChange={(v) => { setNegocio(v); setFaixa("todas"); }}>
            <SelectTrigger data-testid="filter-negocio" className={selectCls}>
              <SelectValue placeholder="Negócio" />
            </SelectTrigger>
            <SelectContent className={contentCls}>
              <SelectItem value="todos">Venda e aluguel</SelectItem>
              <SelectItem value="Venda">Comprar</SelectItem>
              <SelectItem value="Aluguel">Alugar</SelectItem>
            </SelectContent>
          </Select>

          <Select value={tipo} onValueChange={setTipo}>
            <SelectTrigger data-testid="filter-tipo" className={selectCls}>
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent className={contentCls}>
              <SelectItem value="todos">Todos os tipos</SelectItem>
              {TIPOS.map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={cidade} onValueChange={setCidade}>
            <SelectTrigger data-testid="filter-cidade" className={selectCls}>
              <SelectValue placeholder="Cidade" />
            </SelectTrigger>
            <SelectContent className={contentCls}>
              <SelectItem value="todas">Todas as cidades</SelectItem>
              {CIDADES_ATENDIDAS.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={faixa} onValueChange={setFaixa}>
            <SelectTrigger data-testid="filter-preco" className={selectCls}>
              <SelectValue placeholder="Faixa de preço" />
            </SelectTrigger>
            <SelectContent className={contentCls}>
              {faixas.map((f) => (
                <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={quartos} onValueChange={setQuartos}>
            <SelectTrigger data-testid="filter-quartos" className={selectCls}>
              <SelectValue placeholder="Quartos" />
            </SelectTrigger>
            <SelectContent className={contentCls}>
              {QUARTOS.map((q) => (
                <SelectItem key={q.value} value={q.value}>{q.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <button
            onClick={limpar}
            data-testid="filter-reset-button"
            className="flex h-12 items-center justify-center gap-2 border border-white/10 font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400 transition-colors duration-300 hover:border-brand/50 hover:text-brand"
          >
            <RotateCcw size={13} /> Limpar
          </button>
        </div>
      </Reveal>

      <p className="mt-10 font-mono text-[11px] uppercase tracking-[0.25em] text-stone-500" data-testid="results-count">
        {loading ? "Carregando..." : `${imoveis.length} ${imoveis.length === 1 ? "imóvel encontrado" : "imóveis encontrados"}`}
      </p>

      <motion.div layout className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        <AnimatePresence mode="popLayout">
          {imoveis.map((p, i) => (
            <PropertyCard key={p.slug} property={p} index={i} />
          ))}
        </AnimatePresence>
      </motion.div>

      {!loading && imoveis.length === 0 && (
        <div data-testid="empty-state" className="mt-8 flex flex-col items-center border border-dashed border-white/15 py-20 text-center">
          <Building2 size={32} className="text-brand/60" />
          <p className="mt-4 font-serif text-xl text-stone-300">Nenhum imóvel com estes critérios.</p>
          <p className="mt-2 text-sm font-light text-stone-500">
            Ajuste os filtros ou{" "}
            <Link to="/contato" data-testid="empty-state-contact-link" className="text-brand underline-offset-4 hover:underline">
              fale conosco
            </Link>{" "}
            — buscamos o imóvel certo para você.
          </p>
        </div>
      )}
    </main>
  );
};

export default Imoveis;
